'use client';

import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { cn } from '../../lib/cn';
import { SIcon } from '../SIcon';
import { STooltip } from '../STooltip';
import {
  STEPPER_COLORS,
  STEPPER_HORIZONTAL_COMPLETED_COLORS,
  STEPPER_LAYOUT,
  STEPPER_SIZE_CONFIG,
  STEPPER_VERTICAL_COLORS,
  STEPPER_VERTICAL_LAYOUT,
  type SStepperItem,
  type SStepperSize,
} from './stepper.config';

type StepperState = 'completed' | 'active' | 'default';

/** error 아이템 hover 시 노출되는 고정 안내 문구 */
const ERROR_TOOLTIP_MESSAGE = '확인이 필요한 항목이 있습니다.';

export interface SStepperProps {
  /** 단계 목록 */
  items: SStepperItem[];
  /** 현재 활성 단계 value */
  value?: string;
  /** 크기 */
  size?: SStepperSize;
  /** 세로형 여부 */
  vertical?: boolean;
  /** 단계 클릭 가능 여부. 미지정 시 가로형=false, 세로형=true */
  clickable?: boolean;
  /** 단계 선택 시 호출 */
  onValueChange?: (value: string, item: SStepperItem, index: number) => void;
  /** 접근성 레이블 */
  ariaLabel?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * ref 로 노출되는 커스텀 툴팁 제어 API. 세로형 전용 — items 중 error 가 true 인 첫 번째
 * 아이템의 텍스트+뱃지 영역에 표시된다.
 *
 * 지금은 대상(첫 번째 error 아이템 하나)·문구·스타일이 hover 툴팁(ERROR_TOOLTIP_MESSAGE, danger)과
 * 동일하게 고정되어 있다 — 열고 닫는 타이밍만 소비자가 제어한다. 대상 아이템을 지정하거나
 * content·type 을 커스터마이즈하는 기능은 필요해지면 추가한다(지금은 만들지 않음).
 */
export interface SStepperHandle {
  /** error 아이템 중 첫 번째에 툴팁을 연다. error 아이템이 없으면 아무 일도 일어나지 않는다. */
  showItemTooltip: () => void;
  /** 열려있는 커스텀 툴팁을 닫는다. 활성 단계(value prop)가 바뀌면 별도 호출 없이도 자동으로 닫힌다. */
  hideItemTooltip: () => void;
}

function getItemValue(item: SStepperItem, index: number) {
  return item.value ?? String(index + 1);
}

function groupAdjacentItems(items: SStepperItem[]) {
  return items.reduce<Array<{ group?: string; startIndex: number; items: SStepperItem[] }>>(
    (groups, item, index) => {
      const currentGroup = item.group;
      const previous = groups[groups.length - 1];

      if (currentGroup && previous?.group === currentGroup) {
        previous.items.push(item);
        return groups;
      }

      groups.push({ group: currentGroup, startIndex: index, items: [item] });
      return groups;
    },
    [],
  );
}

/** SStepper — sd-stepper 포팅. 단계 진행 상태 표시 전용. */
export const SStepper = forwardRef<SStepperHandle, SStepperProps>(function SStepper(
  {
    items,
    value,
    size = 'sm',
    vertical = false,
    clickable,
    onValueChange,
    ariaLabel = '진행 단계',
    className,
    style,
  },
  ref,
) {
  const config = STEPPER_SIZE_CONFIG[size];
  const isClickable = clickable ?? vertical;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipHoverValue, setTooltipHoverValue] = useState<string | null>(null);
  const [customTooltipOpen, setCustomTooltipOpen] = useState(false);
  const [verticalAccentStripeStyle, setVerticalAccentStripeStyle] = useState<CSSProperties>({
    height: 0,
    opacity: 0,
    transform: 'translateY(0px)',
  });
  const verticalItemRefs = useRef<Array<HTMLElement | null>>([]);
  const activeIndex = Math.max(
    0,
    items.findIndex((item, index) => getItemValue(item, index) === value),
  );
  const lastCompletedIndex = items.reduce(
    (lastIndex, item, index) => (item.completed === true ? index : lastIndex),
    -1,
  );
  const resolvedClickableIndex = lastCompletedIndex + 1;
  const [visitedMaxIndex, setVisitedMaxIndex] = useState(activeIndex);
  const firstErrorIndex = items.findIndex(item => item.error === true);

  useImperativeHandle(
    ref,
    () => ({
      showItemTooltip: () => setCustomTooltipOpen(true),
      hideItemTooltip: () => setCustomTooltipOpen(false),
    }),
    [],
  );

  useLayoutEffect(() => {
    setVisitedMaxIndex(current => Math.max(current, activeIndex));
  }, [activeIndex]);

  // 활성 단계가 바뀌면 커스텀 툴팁은 별도 호출 없이 자동으로 닫힌다.
  useLayoutEffect(() => {
    setCustomTooltipOpen(false);
  }, [activeIndex]);

  useLayoutEffect(() => {
    if (!vertical) return;

    const activeItem = verticalItemRefs.current[activeIndex];
    if (!activeItem) {
      setVerticalAccentStripeStyle(current => ({ ...current, opacity: 0 }));
      return;
    }

    setVerticalAccentStripeStyle({
      height: activeItem.offsetHeight,
      opacity: 1,
      transform: `translateY(${activeItem.offsetTop}px)`,
    });
  }, [activeIndex, vertical, items]);

  const getState = (item: SStepperItem, index: number): StepperState => {
    if (index === activeIndex) return 'active';
    if (item.completed != null) return item.completed ? 'completed' : 'default';
    return 'default';
  };

  const emitValueChange = (item: SStepperItem, index: number) => {
    onValueChange?.(getItemValue(item, index), item, index);
  };

  const isWithinClickableIndex = (index: number) => index <= resolvedClickableIndex;

  const renderStepContent = (
    item: SStepperItem,
    index: number,
    options: { isVertical?: boolean; roleListItem?: boolean; hovered?: boolean } = {},
  ) => {
    const { isVertical = false, roleListItem = true, hovered = false } = options;
    const state = getState(item, index);
    const isHorizontalCompleted = !isVertical && state === 'completed';
    const isVerticalCompleted = isVertical && state === 'completed';
    const isVerticalLastActive = isVertical && state === 'default' && index === visitedMaxIndex;
    const showsCheckIcon = state === 'completed';
    const itemColor = (() => {
      if (isHorizontalCompleted) return STEPPER_HORIZONTAL_COMPLETED_COLORS.text;
      if (!isVertical) return STEPPER_COLORS.text[state];
      if (state === 'active') return STEPPER_COLORS.text.active;
      if (isVerticalCompleted) return STEPPER_VERTICAL_COLORS.textCompleted;
      return STEPPER_VERTICAL_COLORS.textDefault;
    })();
    const sequenceBorderColor = (() => {
      if (isVerticalCompleted) {
        return STEPPER_VERTICAL_COLORS.sequenceBorderCompleted;
      }
      if (isVertical && (state === 'active' || isVerticalLastActive)) {
        return STEPPER_VERTICAL_COLORS.sequenceBorderActive;
      }
      if (isHorizontalCompleted) return STEPPER_HORIZONTAL_COMPLETED_COLORS.sequenceBorder;
      return STEPPER_COLORS.sequenceBorder[state];
    })();
    const sequenceBg = (() => {
      if (isVerticalCompleted) {
        return STEPPER_VERTICAL_COLORS.sequenceBgCompleted;
      }
      if (isVertical && (state === 'active' || isVerticalLastActive)) {
        return STEPPER_VERTICAL_COLORS.sequenceBgActive;
      }
      if (isHorizontalCompleted) return STEPPER_HORIZONTAL_COMPLETED_COLORS.sequenceBg;
      return STEPPER_COLORS.sequenceBg[state];
    })();
    const sequenceColor = (() => {
      if (isVerticalCompleted) {
        return STEPPER_VERTICAL_COLORS.sequenceIconCompleted;
      }
      if (isVertical && (state === 'active' || isVerticalLastActive)) {
        return STEPPER_VERTICAL_COLORS.sequenceTextActive;
      }
      if (isVertical) return STEPPER_VERTICAL_COLORS.sequenceTextDefault;
      if (isHorizontalCompleted) return STEPPER_HORIZONTAL_COMPLETED_COLORS.sequenceIcon;
      return STEPPER_COLORS.sequenceText[state];
    })();
    const itemStyle = {
      gap: STEPPER_LAYOUT.itemGap,
      color: itemColor,
      fontWeight:
        state === 'active' || (isVertical && hovered)
          ? config.fontWeightActive
          : config.fontWeightDefault,
    } as CSSProperties;
    const sequenceStyle = {
      width: config.sequenceSize,
      height: config.sequenceSize,
      flexBasis: config.sequenceSize,
      borderRadius: STEPPER_LAYOUT.sequenceRadius,
      borderWidth: STEPPER_LAYOUT.sequenceBorderWidth,
      borderColor: sequenceBorderColor,
      background: sequenceBg,
      color: sequenceColor,
    } as CSSProperties;

    return (
      <span
        role={roleListItem ? 'listitem' : undefined}
        aria-current={roleListItem && state === 'active' ? 'step' : undefined}
        className="inline-flex items-center whitespace-nowrap"
        style={itemStyle}
      >
        <span
          aria-hidden
          className="box-border inline-flex shrink-0 items-center justify-center border border-solid font-bold"
          style={sequenceStyle}
        >
          {showsCheckIcon ? (
            <SIcon name="check" size={STEPPER_LAYOUT.sequenceIcon} color="currentColor" />
          ) : (
            index + 1
          )}
        </span>
        <span>{item.label}</span>
      </span>
    );
  };

  const renderClickableStepContent = (item: SStepperItem, index: number, isVertical = false) => {
    const isDisabled = !isWithinClickableIndex(index);

    return (
      <button
        type="button"
        disabled={isDisabled}
        className={cn(
          'm-0 inline-flex w-full items-center border-0 bg-transparent p-0 text-left',
          isDisabled ? 'cursor-default' : 'cursor-pointer',
        )}
        style={{ font: 'inherit', color: 'inherit' }}
        onClick={() => {
          if (!isDisabled) emitValueChange(item, index);
        }}
      >
        {renderStepContent(item, index, { isVertical, roleListItem: false })}
      </button>
    );
  };

  if (vertical) {
    const groups = groupAdjacentItems(items);

    return (
      <div
        role="list"
        aria-label={ariaLabel}
        className={cn('relative inline-flex flex-col items-start', className)}
        style={{
          width: STEPPER_VERTICAL_LAYOUT.width,
          fontSize: config.fontSize,
          lineHeight: config.lineHeight,
          ...style,
        }}
      >
        <span
          aria-hidden="true"
          data-stepper-accent-stripe="true"
          className="pointer-events-none absolute left-0 top-0 z-[1]"
          style={{
            width: STEPPER_VERTICAL_LAYOUT.accentStripeWidth,
            background: STEPPER_VERTICAL_LAYOUT.accentStripeColor,
            transition: 'transform 160ms ease, height 160ms ease, opacity 120ms ease',
            ...verticalAccentStripeStyle,
          }}
        />
        {groups.map(group => {
          const groupKey = group.group ?? getItemValue(group.items[0], group.startIndex);

          return (
            <div
              key={`${groupKey}-${group.startIndex}`}
              className="flex w-full flex-col items-start"
            >
              {group.group && (
                <div
                  className="flex w-full items-center overflow-hidden"
                  style={{
                    gap: STEPPER_VERTICAL_LAYOUT.itemGap,
                    paddingLeft: STEPPER_VERTICAL_LAYOUT.itemPaddingX,
                    paddingRight: STEPPER_VERTICAL_LAYOUT.itemPaddingX,
                    paddingTop: STEPPER_VERTICAL_LAYOUT.titlePaddingTop,
                    paddingBottom: STEPPER_VERTICAL_LAYOUT.titlePaddingBottom,
                    background: STEPPER_VERTICAL_COLORS.itemBg.default,
                    color: STEPPER_VERTICAL_COLORS.titleText,
                    fontSize: STEPPER_VERTICAL_LAYOUT.titleFontSize,
                    lineHeight: STEPPER_VERTICAL_LAYOUT.titleLineHeight,
                    fontWeight: 700,
                  }}
                >
                  <span className="whitespace-nowrap">{group.group}</span>
                </div>
              )}
              {group.items.map((item, itemIndex) => {
                const index = group.startIndex + itemIndex;
                const state = getState(item, index);
                const itemValue = getItemValue(item, index);
                const canClickItem = isClickable && isWithinClickableIndex(index);
                const isDisabled = !canClickItem;
                const isHovered = hoveredIndex === index && canClickItem && state !== 'active';
                const itemContainerStyle = {
                  gap: STEPPER_VERTICAL_LAYOUT.itemGap,
                  paddingLeft: STEPPER_VERTICAL_LAYOUT.itemPaddingX,
                  paddingRight: STEPPER_VERTICAL_LAYOUT.itemPaddingX,
                  paddingTop: STEPPER_VERTICAL_LAYOUT.itemPaddingY,
                  paddingBottom: STEPPER_VERTICAL_LAYOUT.itemPaddingY,
                  background:
                    state === 'active'
                      ? STEPPER_VERTICAL_COLORS.itemBg.selected
                      : isHovered
                        ? STEPPER_VERTICAL_COLORS.itemBg.hover
                        : STEPPER_VERTICAL_COLORS.itemBg.default,
                } as CSSProperties;
                const handleMouseEnter = () => {
                  if (canClickItem) setHoveredIndex(index);
                };
                const handleMouseLeave = () => {
                  setHoveredIndex(current => (current === index ? null : current));
                };
                const stepContent = renderStepContent(item, index, {
                  isVertical: true,
                  roleListItem: false,
                  hovered: isHovered,
                });
                const errorBadge = item.error ? (
                  <span
                    aria-hidden="true"
                    data-stepper-error-badge="true"
                    className="inline-flex shrink-0 overflow-hidden rounded-full"
                    style={{
                      width: 6,
                      height: 6,
                      background: STEPPER_VERTICAL_COLORS.errorBadge,
                    }}
                  />
                ) : null;

                // hover 툴팁과 커스텀 툴팁(ref API)은 지금은 문구·스타일이 완전히 같은 고정 안내라,
                // 둘 중 하나라도 열려있으면 그냥 보여준다(둘 다 error 아이템에만 뜬다).
                // TODO: 커스텀 툴팁에 별도 문구/스타일이 필요해지면 열림 조건을 다시 분리한다.
                const isTooltipHovered = tooltipHoverValue === itemValue;
                const isCustomTooltipOpen = customTooltipOpen && index === firstErrorIndex;
                const showTooltip =
                  item.error === true && (isTooltipHovered || isCustomTooltipOpen);
                const handleTooltipMouseEnter = () => setTooltipHoverValue(itemValue);
                const handleTooltipMouseLeave = () => {
                  setTooltipHoverValue(current => (current === itemValue ? null : current));
                };

                const labelBadge = (
                  <STooltip
                    trigger="none"
                    open={showTooltip}
                    content={ERROR_TOOLTIP_MESSAGE}
                    tooltipType="danger"
                    placement="right"
                  >
                    {/* onClick 은 겹쳐진 button(z-[2])과 같은 동작의 포인터 캡처용 중복일 뿐이며,
                        키보드 접근은 그 button 이 이미 제공한다 */}
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <span
                      className={cn(
                        'relative z-[3] inline-flex items-center',
                        isClickable && (isDisabled ? 'cursor-default' : 'cursor-pointer'),
                      )}
                      style={{ gap: STEPPER_VERTICAL_LAYOUT.itemGap }}
                      onMouseEnter={handleTooltipMouseEnter}
                      onMouseLeave={handleTooltipMouseLeave}
                      onClick={
                        isClickable
                          ? () => {
                              if (!isDisabled) emitValueChange(item, index);
                            }
                          : undefined
                      }
                    >
                      {stepContent}
                      {errorBadge}
                    </span>
                  </STooltip>
                );

                if (isClickable) {
                  return (
                    <div
                      key={itemValue}
                      ref={node => {
                        verticalItemRefs.current[index] = node;
                      }}
                      role="listitem"
                      aria-current={state === 'active' ? 'step' : undefined}
                      className={cn(
                        'relative flex w-full items-center overflow-hidden',
                        isDisabled ? 'cursor-default' : 'cursor-pointer',
                      )}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      style={itemContainerStyle}
                    >
                      <button
                        type="button"
                        disabled={isDisabled}
                        aria-label={item.label}
                        className={cn(
                          'absolute inset-0 z-[2] m-0 border-0 bg-transparent p-0 text-left outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-(--sys-color-border-accent)',
                          isDisabled ? 'cursor-default' : 'cursor-pointer',
                        )}
                        onClick={() => {
                          if (!isDisabled) emitValueChange(item, index);
                        }}
                      />
                      {labelBadge}
                    </div>
                  );
                }

                return (
                  <div
                    key={itemValue}
                    ref={node => {
                      verticalItemRefs.current[index] = node;
                    }}
                    role="listitem"
                    aria-current={state === 'active' ? 'step' : undefined}
                    className={cn(
                      'relative flex w-full items-center overflow-hidden',
                      isClickable && (canClickItem ? 'cursor-pointer' : 'cursor-default'),
                    )}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    style={itemContainerStyle}
                  >
                    {labelBadge}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="list"
      aria-label={ariaLabel}
      className={cn('inline-flex items-center', className)}
      style={{
        gap: STEPPER_LAYOUT.containerGap,
        fontSize: config.fontSize,
        lineHeight: config.lineHeight,
        ...style,
      }}
    >
      {items.map((item, index) => {
        const state = getState(item, index);
        const content = isClickable ? (
          <span role="listitem" aria-current={state === 'active' ? 'step' : undefined}>
            {renderClickableStepContent(item, index)}
          </span>
        ) : (
          renderStepContent(item, index)
        );

        return (
          <span
            key={getItemValue(item, index)}
            className="inline-flex items-center"
            style={{ gap: STEPPER_LAYOUT.containerGap }}
          >
            {content}
            {index < items.length - 1 && (
              <span
                aria-hidden
                className="inline-flex h-px shrink-0 items-center"
                style={{ width: STEPPER_LAYOUT.dashWidth, color: STEPPER_COLORS.dash }}
              >
                <svg
                  width="20"
                  height="1"
                  viewBox="0 0 20 1"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* 색은 부모 span 의 color(= --cmp-stepper-horizontal-dash-color)를 물려받는다 */}
                  <rect width="1.6" height="1" fill="currentColor" />
                  <rect x="5" width="3.3" height="1" fill="currentColor" />
                  <rect x="11.7" width="3.3" height="1" fill="currentColor" />
                  <rect x="18.4" width="1.6" height="1" fill="currentColor" />
                </svg>
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
});
